<template>
  <div class="d-flex CARD">
    <router-link
        :to="{
          name: 'dashboard::pro::jobs',
          params: {
            jobId: isSearchStatusAlias ? undefined : jobId,
            jobsStatusAlias,
          },
          query: {
            [$options.JOBS_QUERY_PARAMS.IDS]: isSearchStatusAlias ? [jobId] : undefined,
          },
        }"
        class="APPOINTMENT-LIST-ITEM flex-1-1">
      <div class="TIME" selenium="agenda-time">
        <div class="DATE-HEADING">
          <span
              v-if="$options.isToday(new Date(appointment.start_dtm))"
              class="text-lg text-bold c-depot">Today</span>
          <span
              v-else-if="$options.isTomorrow(new Date(appointment.start_dtm))"
              class="text-lg text-bold">Tomorrow</span>
          <span v-else>
            {{ appointment.start_dtm | calendarDate({ weekday: true, year: false }) }}</span>
        </div>
        <span v-if="isAllDay" class="text-lg c-gray70" selenium="agenda-time-all-day">All-day</span>
        <template v-else>
          <span class="text-lg c-gray70" selenium=agenda-time-start>
            {{ appointment.start_dtm | timeOfDay }}
          </span>
          <span class="text-lg c-gray60" selenium="agenda-time-end">
            {{ appointment.end_dtm | timeOfDay }}
          </span>
        </template>
      </div>
      <div class="d-flex flex-1-1 fd-column ov-hidden p-l-2">
        <transition name="FADE" mode="out-in">
          <job-list-item-details
              v-if="job"
              :full-name="job.location.first_name | fullName(job.location.last_name)"
              :serviceName="job.service_name"
              :location="appointment.address || job.location | location(multiline=true)"
          />
          <job-list-item-placeholder v-else />
        </transition>
        <appointment-bubble
            active
            showType
            :purpose="appointment.purpose || ''"
            :purposeDescription="appointment.purpose_description || ''" />
      </div>
    </router-link>
    <div class=" flex-0-0 m-x-0:mo p-r-3">
      <vk-listbox
          display-text="..."
          class="OPTIONS"
          menuClass="MENU"
          value=""
          @input="handleSelection">
        <vk-listbox-option
            v-for="(option, index) in options"
            class="OPTION bd-x-gray50 text-bold"
            :class="{
              'bd-t-gray50':index === 0,
              'c-depot':index ===options.length -1
            } "
            :key="index + option"
            :value="option">
          {{ option }}
        </vk-listbox-option>
      </vk-listbox>
    </div>
  </div>
</template>

<script>
import { differenceInHours, isToday, isTomorrow } from 'date-fns';
import fullName from '@/filters/full-name';
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import subtask from '@/mixins/job/subtask';
import appointment from '@/mixins/appointment';
import JobListItemDetails from '@/views/Dashboard/components/JobListItemDetails';
import JobListItemPlaceholder from '@/views/Dashboard/components/JobListItemPlaceholder';
import AppointmentBubble from '@/views/Dashboard/components/AppointmentBubble';

const HOURS_IN_DAY = 24;

export default {
  isToday,
  isTomorrow,
  name: 'appointment-list-item',
  components: {
    JobListItemDetails,
    JobListItemPlaceholder,
    AppointmentBubble,
  },
  mixins: [
    jobParams,
    job,
    subtask,
    appointment,
  ],
  props: {
    id: [String, Number],
  },
  data () {
    return {
      options: ['Edit', 'Export', 'Delete'],
    };
  },
  computed: {
    // Used by appointment mixin.
    appointmentId () {
      return this.id;
    },
    // Used by job mixin.
    jobId () {
      return this.appointment.order_hash;
    },
    // Used by subtask mixin.
    subtaskId () {
      return this.job.subtask;
    },
    jobsStatusAlias () {
      let result = this.$options.JOBS_STATUS_ALIASES.search.key;

      if (this.match) {
        const allStatusAliases = Object.values(
          this.$options.JOBS_STATUS_ALIASES
        );
        const jobsStatusAlias = allStatusAliases.find(alias =>
          alias.statuses.includes(this.match.status)
        );

        if (jobsStatusAlias) {
          result = jobsStatusAlias.key;
        }
      }

      return result;
    },
    isAllDay () {
      const startDateTime = new Date(this.appointment.start_dtm);
      const endDateTime = new Date(this.appointment.end_dtm);
      const hours = startDateTime.getHours();
      const minutes = startDateTime.getMinutes();
      const diff = differenceInHours(endDateTime, startDateTime);

      return (
        !hours && !minutes && diff >= HOURS_IN_DAY && !(diff % HOURS_IN_DAY)
      );
    },
  },
  methods: {
    handleSelection (selectedOption) {
      switch (selectedOption) {
        case 'Delete':
          this.$events.$emit(this.$events.APPOINTMENT_DOTS_DELETE_CLICK);
          this.deleteAppointment();
          break;
        case 'Export':
          this.$events.$emit(this.$events.APPOINTMENT_DOTS_EXPORT_CLICK);
          this.onAppointmentsExport();
          break;
        default:
          this.$events.$emit(this.$events.APPOINTMENT_DOTS_EDIT_CLICK);
          this.$router.push({
            name: 'dashboard::pro::jobs',
            params: {
              jobId: this.isSearchStatusAlias ? undefined : this.jobId,
              jobsStatusAlias: this.jobsStatusAlias,
            },
            query: {
              [this.$options.JOBS_QUERY_PARAMS.IDS]: this.isSearchStatusAlias
                ? [this.jobId] : undefined,
            },
          });
      }
    },
    async onAppointmentsExport () {
      const { first_name, last_name } = this.job.location;
      const customersFullName = fullName(first_name, last_name);
      const { data, headers } = await this.exportAppointments({
        appointmentId: this.appointment.appointment_id,
        serviceProviderId: this.appointment.service_provider_id,
      });
      const blob = new Blob([data], { type: headers['Content-Type'] });
      const link = global.document.createElement('a');
      link.href = global.window.URL.createObjectURL(blob);
      link.download = `${customersFullName} - ${this.job.service_name}.ics`.replace(/\s/g, '_');

      link.click();
    },
    deleteAppointment () {
      this.$emit('onDelete', {
        appointment: this.appointment,
        jobId: this.job.id,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";
  @import "~@/views/Dashboard/constants";
  @import "~@/views/Dashboard/tools";

  .APPOINTMENT-LIST-ITEM {
    @include hover(text-decoration, none, none);
    @include desktop-hover(background-color, transparent, $color-gray20);
    @include pseudo(visited, color, $color-gray80, $color-gray80);
    display: flex;
    flex: 0 0 auto;
    min-height: $appointment-list-item-min-height;
    padding: 3*$unit;
    background-color: white;
    will-change: background-color;
    transition: background-color $transition-half-duration;
    @include mobile-only () {
      width:90vw;
    }
  }

  .CARD {
    flex: 1 1 auto;
    justify-content: space-between;
    @include mobile-only () {
      width:100vw;
    }
    border-bottom: 1px solid $color-gray30;
  }
  .TIME {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    width: 14.25 * $unit;
    padding-right: 2*$unit;
    border-right: 6px solid $color-depot;
    text-align: left;
    border-color: #F96302;
  }

  .OPTIONS {
    @include text-medium;

    /deep/ .input {
      height: 100px;
      width: 50px;
      padding: 0;
      border: none;
      font-weight: 700;
      font-size: 34px;
      color: $color-depot;
      font-family: serif;
      align-items: start;
      margin-left: $unit;

      .VK-LISTBOX-TEXT {
        padding-right: $unit;
      }

      .CHEVRON {
        color: transparent;
      }
    }

    /deep/ .MENU {
      top: 3*$unit;
      right: 0;
      height: auto;
      width: 100px;
      padding: $unit 0;
      border: none;
      overflow: auto;
      background-color: transparent;

    }
  }
  .OPTION {
    background-color: white;
    border-bottom: solid 1px $color-gray50;
    @include hover(transform, none, none);
    transition: none;

  }
  .VK-LISTBOX-OPTION.SELECTED {
    background-color: white;
    color: $color-gray70
  }
  .DATE-HEADING {
    align-items: left;
    transition: all $transition-duration;
  }
  .VK-LISTBOX-TEXT {
        font-size:large;
        color: $color-depot;
      }
  .BUTTON{
    min-height: $appointment-list-item-min-height;
  }
</style>
